import React, { useEffect, useMemo, useState } from 'react'
import SearchBar from 'pages/pga-coach/students/search-bar'
import { makeStyles } from '@material-ui/styles'
import FacilityListItem from './facility-list-item'
import EnhancedTable from 'components/tables/enhanced-table'
import TablePagination from 'components/pagination/table-pagination'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import SectionEmptyState from 'components/section-empty-state'
import TipWell, { TipWellActionButton } from 'components/tip-well'
import { Box, TableContainer } from '@material-ui/core'

const FACILITY_HELP_URL =
  'https://support.pga.com/en/articles/6740181-managing-location-on-coach-search-setting-a-primary-facility'

const useStyles = makeStyles(theme => ({
  containerStyles: {
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4.5, 2),
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
      padding: theme.spacing(3, 1, 2),
    },
  },
  tipBanner: {
    marginBottom: theme.spacing(2),
  },
  tableShell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '1 1 0',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      width: '100%',
    },
  },
  searchField: {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 360,
    width: '100%',
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 'auto',
    },
  },
  tableContent: {
    padding: theme.spacing(0, 1, 1),
  },
  listEmptyStateContent: {
    padding: theme.spacing(4, 2),
  },
}))

const sortByFacilityName = (a, b, dir) => {
  const left = (a.props.facility?.name || '').toLowerCase().replace(/\s/g, '')
  const right = (b.props.facility?.name || '').toLowerCase().replace(/\s/g, '')

  if (left > right) return dir === 'desc' ? 1 : -1
  if (left < right) return dir === 'desc' ? -1 : 1
  return 0
}

const matchesSearchTerm = (facility, term) => {
  const normalizedTerm = term.toLowerCase().trim()
  if (!normalizedTerm) return true

  const searchableFields = [
    facility.name,
    facility.address1,
    facility.city,
    facility.state,
    facility.zip,
  ]

  return searchableFields.some(field =>
    field?.toLowerCase().includes(normalizedTerm),
  )
}

export default function FacilityListView({
  facilities,
  hasCoachProfile,
  refetch,
}) {
  const classes = useStyles()
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const rowsPerPage = 25

  const filteredFacilities = useMemo(
    () =>
      facilities.filter(facility => matchesSearchTerm(facility, searchTerm)),
    [facilities, searchTerm],
  )

  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: '80%',
      mobile: true,
      sortable: true,
      sortComparator: sortByFacilityName,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: '20%',
      mobile: true,
      sortable: false,
    },
  ]

  const pageCount =
    Math.ceil((filteredFacilities?.length || 0) / rowsPerPage) || 1

  const rows = filteredFacilities.map(facility => (
    <FacilityListItem
      facility={facility}
      key={facility.id}
      hasCoachProfile={hasCoachProfile}
      refetch={refetch}
    />
  ))

  return (
    <TableContainer className={classes.containerStyles}>
      {hasCoachProfile && (
        <Box className={classes.tipBanner}>
          <TipWell
            icon={InfoOutlinedIcon}
            caption="View and manage the facilities that are showcased on your profile. Your primary facility and its address is used for location-based searches on pga.com."
            action={
              <TipWellActionButton
                href={FACILITY_HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
                component="a"
              >
                Learn More
              </TipWellActionButton>
            }
          />
        </Box>
      )}
      <Box className={classes.tableShell}>
        <Box className={classes.toolbar}>
          <Box className={classes.searchGroup}>
            <SearchBar
              className={classes.searchField}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder="Search Facilities"
              data-testid="facility-list-search"
              style={{ margin: 0 }}
            />
          </Box>
          <Box className={classes.toolbarActions}>
            {filteredFacilities.length > 0 && (
              <TablePagination
                page={page}
                pageCount={pageCount}
                rowsPerPage={rowsPerPage}
                totalCount={filteredFacilities.length}
                onPageChange={setPage}
                alwaysShowCount
              />
            )}
          </Box>
        </Box>
        <Box className={classes.tableContent}>
          <EnhancedTable
            rows={rows}
            columns={columns}
            label="Facilities"
            numberOfRows={rowsPerPage}
            variant="list"
            rowHeight="72px"
            initialOrderBy="name"
            initialOrder="asc"
            page={page}
            onPageChange={setPage}
            showHeaderPagination={false}
            showPaginator={false}
            emptyState={() => (
              <Box className={classes.listEmptyStateContent}>
                <SectionEmptyState
                  Icon={RoomOutlinedIcon}
                  title="Facilities"
                  caption="No matching facilities."
                  bordered={false}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </TableContainer>
  )
}
